import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { Button, Card, Select } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { CallButton } from '@/phone/CallButton';
import { dateTime } from '@/lib/format';
import { addContactNote, getContact, linkContactCompany, listCompanies, unlinkContactCompany } from '@/data/client';
import { ContactFormModal } from '@/components/crm/ContactForm';
import { LifecycleBadge } from '@/components/crm/LifecycleBadge';
import { FormError, Modal } from '@/components/crm/Modal';
import { NoteComposer, Timeline } from '@/components/crm/Timeline';
import { MeetingsPanel } from '@/components/crm/MeetingsPanel';

export function ContactDetail() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['contact', id], queryFn: () => getContact(id) });
  const [editing, setEditing] = useState(false);
  const [linking, setLinking] = useState(false);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['contact', id] });
    void qc.invalidateQueries({ queryKey: ['contacts'] });
    void qc.invalidateQueries({ queryKey: ['company'] });
    void qc.invalidateQueries({ queryKey: ['companies'] });
  };
  const unlink = useMutation({ mutationFn: (companyId: string) => unlinkContactCompany(id, companyId), onSuccess: refresh });

  if (isLoading) return null;
  if (!data) return <div className="dash-empty"><h2>Contact not found</h2><Link to="/contacts">Back to contacts</Link></div>;
  const { contact, companies, timeline } = data;

  return (
    <div className="crm-record">
      <Link to="/contacts" className="cq-back"><Icon name="arrow-left" size={16} /> Contacts</Link>
      <div className="crm-record__head">
        <div className="crm-record__title">
          <h2>{contact.name ?? contact.email ?? contact.phone}</h2>
          <div className="crm-record__sub">
            {contact.title && <span>{contact.title}</span>}
            {contact.email && <a href={`mailto:${contact.email}`}>{contact.email}</a>}
            {contact.phone && <CallButton to={contact.phone} name={contact.name ?? contact.phone} />}
          </div>
        </div>
        <div className="crm-record__actions">
          <Button variant="outline" size="sm" iconLeft={<Icon name="edit" size={14} />} onClick={() => setEditing(true)}>Edit</Button>
        </div>
      </div>

      <div className="crm-record__cols">
        <Card padding={false} className="crm-section">
          <div className="crm-section__head"><h3>Timeline</h3></div>
          <NoteComposer onSave={(body) => addContactNote(id, body).then(refresh)} />
          <Timeline items={timeline} />
        </Card>

        <div className="crm-record__side">
          <MeetingsPanel filter={{ contactId: contact.id }} companyId={companies.find((c) => c.isPrimary)?.id ?? companies[0]?.id}
            defaultTitle={`Call with ${contact.name ?? contact.email ?? 'contact'}`}
            guests={[{ contactId: contact.id, name: contact.name ?? contact.email ?? 'Contact', email: contact.email }]} />

          <Card padding={false} className="crm-section">
            <div className="crm-section__head">
              <h3>Companies</h3>
              <Button variant="ghost" size="sm" iconLeft={<Icon name="plus" size={14} />} onClick={() => setLinking(true)}>Link</Button>
            </div>
            {companies.length ? (
              <ul className="crm-people">
                {companies.map((co) => (
                  <li key={co.id}>
                    <div>
                      <Link to={`/companies/${co.id}`}>{co.name}</Link>
                      {co.isPrimary && <span className="crm-cell-sub">Primary contact</span>}
                    </div>
                    <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <LifecycleBadge lifecycle={co.lifecycle} />
                      <button type="button" className="lead-modal__x" aria-label={`Unlink ${co.name}`} onClick={() => unlink.mutate(co.id)}>
                        <Icon name="x" size={14} />
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            ) : <p className="crm-cell-muted">Not linked to a company.</p>}
          </Card>

          <Card padding={false} className="crm-section">
            <div className="crm-section__head"><h3>Details</h3></div>
            <dl className="crm-fields">
              <dt>Owner</dt><dd>{contact.ownerName ?? 'Unassigned'}</dd>
              <dt>Created</dt><dd>{dateTime(contact.createdAt)}</dd>
            </dl>
          </Card>
        </div>
      </div>

      {editing && <ContactFormModal contact={contact} onClose={() => setEditing(false)} />}
      {linking && <LinkCompanyModal contactId={id} exclude={companies.map((c) => c.id)} onDone={refresh} onClose={() => setLinking(false)} />}
    </div>
  );
}

function LinkCompanyModal({ contactId, exclude, onDone, onClose }: {
  contactId: string; exclude: string[]; onDone: () => void; onClose: () => void;
}) {
  const { data } = useQuery({ queryKey: ['companies', {}], queryFn: () => listCompanies() });
  const [companyId, setCompanyId] = useState('');
  const link = useMutation({
    mutationFn: () => linkContactCompany(contactId, companyId),
    onSuccess: () => { onDone(); onClose(); },
  });
  const options = (data ?? []).filter((c) => !exclude.includes(c.id)).map((c) => ({ value: c.id, label: c.name }));
  return (
    <Modal
      title="Link to company"
      onClose={onClose}
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={() => link.mutate()} disabled={!companyId || link.isPending}>Link</Button>
      </>}
    >
      <Select label="Company" options={options} placeholder="Choose a company" value={companyId} onChange={(e) => setCompanyId(e.target.value)} />
      <FormError error={link.error} />
    </Modal>
  );
}
